import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useEffect, useState } from "react";
import { settings } from "@config/settings";
import { localesParserDictionary } from "@utils/locales-parser";
import { EnsayoThumbnail } from "src/presentation/components/organisms/ensayo-thumbnail";
import { useTranslation } from "next-i18next";
import styles from "./ensayos-page.module.scss";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { Cultivo } from "@models/cultivos";
import { Producto } from "@models/producto";
import { EnsayosDropdown } from "src/presentation/components/organisms/ensayos-dropdown";
import { useProductos } from "src/context/useProductos";
import { useCultivos } from "src/context/useCultivos";
import { useEnsayos } from "src/context/useEnsayos";
import { usePaises } from "src/context/usePaises";
import { Ensayo } from "@models/ensayo";
import { NextSeo } from "next-seo";
import { Text } from "src/presentation/components/atoms";
import { MainRouter } from "@config/urls";

const EnsayosPage: NextPage = (props: any) => {
  const { country } = props;

  const ensayos = useEnsayos();
  const productos = useProductos();
  const cultivos = useCultivos();
  const paises = usePaises();

  const [cultivoFilter, setCultivoFilter] = useState<Cultivo | null>(null);
  const [productoFilter, setProductoFilter] = useState<Producto | null>(null);
  const [cultivosFiltered, setCultivosFiltered] = useState<Array<Cultivo>>([]);
  const [productosFiltered, setProductosFiltered] = useState<Array<Producto>>(
    []
  );

  const [ensayosFiltered, setEnsayosFiltered] =
    useState<Array<Ensayo>>(ensayos);

  useEffect(() => {
    const newProducts = ensayosFiltered
      .map((ensayo) => {
        return ensayo.productos.map((producto) => producto.producto.slug);
      })
      .flat();
    const uniqueProducts = newProducts.filter(
      (item, pos) => newProducts.indexOf(item) == pos
    );

    setProductosFiltered(
      productos.filter((producto) => {
        return uniqueProducts.includes(producto.slug);
      })
    );
  }, [cultivos, ensayosFiltered, productos]);

  useEffect(() => {
    const results = ensayos
      .filter((item) => {
        if (!cultivoFilter) return true;
        return item.cultivo === cultivoFilter.id;
      })
      .filter((item) => {
        if (!productoFilter) return true;
        return item.productos.some(
          (p) => p.producto.slug === productoFilter.slug
        );
      });
    setEnsayosFiltered(results);
  }, [cultivoFilter, ensayos, productoFilter]);

  useEffect(() => {
    setCultivosFiltered(cultivos);
    setProductosFiltered(productos);
  }, [cultivos, productos]);

  const { t } = useTranslation();
  const handleDropdownChange = (prop: any, value: any) => {
    if (prop === "cultivo") {
      setCultivoFilter(value);
    } else if (prop === "producto") {
      setProductoFilter(value);
    }
  };

  const TopBanner = () => {
    return (
      <section
        style={{
          backgroundImage: `linear-gradient(359deg,rgba(0,0,0,.66),hsla(0,0%,100%,.15)),url(/static/images/ensayos-manvert-bioestimulantes.webp)`,
        }}
        className={styles.topbannerSection}
      >
        <Text as="h1">{t("EnsayosView.heading")}</Text>
        <div className={styles.dropdowncontainer}>
          <EnsayosDropdown
            productoInicial={productoFilter}
            cultivoInicial={cultivoFilter}
            onChange={handleDropdownChange}
            productos={productosFiltered ? productosFiltered : []}
            cultivos={cultivosFiltered ? cultivosFiltered : []}
          />
        </div>
      </section>
    );
  };

  return (
    <>
      <NextSeo
        title={t("SEOMeta.EnsayosView.title") || ""}
        description={t("SEOMeta.EnsayosView.title") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.ensayos"),
            url: MainRouter.ensayos.generatePath(),
          },
        ]}
      >
        <div className={styles.root}>
          <TopBanner />
          <section className={styles.ensayosSection}>
            <Text as="h2">{t("EnsayosView.destacados")}</Text>
            <div className={styles.ensayoslist}>
              {ensayosFiltered.map((item) => {
                return (
                  <div
                    key={`${item.id}_${item.nombre}`}
                    className={styles.ensayocontainer}
                  >
                    <EnsayoThumbnail
                      id={item.id}
                      slug={item.slug}
                      title={item.nombre}
                      image={item.imagen}
                      country={country}
                      subtitle={`${t("EnsayosView.realizado")} ${
                        paises.find((x) => x.iso === item.pais)?.nombre
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, query } = Props;
  const host = `https://${req.headers.host}`;

  const { country } = query;

  const countryCode = country?.toString() || settings.DEFAULT_COUNTRY;

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: countryCode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    paises: true,
    ensayos: true,
    productosDestacados: true,
    productosGenericos: true,
  });
  return {
    props: {
      country: countryCode,
      host: host,
      paisesData: JSON.stringify(entities.paisesData),
      ensayosData: JSON.stringify(entities.ensayosData),
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default EnsayosPage;
